// The front door to the course console.
//
// Wraps the whole console: if nobody is signed in, this is ALL you get — no event list, no
// hints about what exists behind it. Once there is a session, it steps out of the way and
// renders its children.
//
// Three modes on one card (sign in / create account / forgot password) rather than three
// routes, because a course manager landing here has exactly one question — "how do I get
// in" — and bouncing them between URLs to answer it is how people give up.

import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { errText } from '../lib/supabase';
import { sendPasswordReset, signIn, signUp, useSession } from '../lib/useSession';
import { LoadingAuth } from './Loading';

type Mode = 'signin' | 'signup' | 'reset';

export default function AuthGate({ children }: { children: ReactNode }) {
  const { session, loading } = useSession();

  // Don't flash a sign-in form at somebody who already has a valid token in storage.
  if (loading) {
    return <LoadingAuth />;
  }

  if (session) return <>{children}</>;
  return <SignInCard />;
}

function SignInCard() {
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function switchTo(next: Mode) {
    setMode(next);
    setErr(null);
    setNotice(null);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setNotice(null);

    const addr = email.trim();
    if (!addr) return setErr('Enter your email address.');
    if (mode !== 'reset' && password.length < 6) {
      return setErr('Password must be at least 6 characters.');
    }

    setBusy(true);
    try {
      if (mode === 'signin') {
        await signIn(addr, password);
        // No success message needed — the session flips and AuthGate swaps in the console.
      } else if (mode === 'signup') {
        const out = await signUp(addr, password);
        if (out.kind === 'check-email') {
          // Email confirmation is on. They are NOT logged in yet, and saying otherwise
          // would leave them staring at a sign-in form wondering what went wrong.
          setNotice(
            `Account created. Check ${out.email} for a confirmation link — you'll need to click it before you can sign in.`,
          );
          setPassword('');
        } else if (out.kind === 'already-registered') {
          setErr('That email already has an account. Try signing in, or reset the password.');
        }
        // 'signed-in' needs no message; the console appears.
      } else {
        await sendPasswordReset(addr);
        setNotice(`If ${addr} has an account, a reset link is on its way.`);
      }
    } catch (e2) {
      setErr(errText(e2));
    } finally {
      setBusy(false);
    }
  }

  const title =
    mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Create a staff account' : 'Reset password';
  const cta =
    mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Create account' : 'Send reset link';

  return (
    <div className="wrap" style={{ maxWidth: 460 }}>
      <div style={{ margin: '32px 0 20px' }}>
        <h1 style={{ fontSize: 30, marginBottom: 4 }}>4sum Tournaments</h1>
        <p className="muted" style={{ margin: 0 }}>
          Course console — run outings, hand out links, watch the board.
        </p>
      </div>

      <div className="card">
        <h2 style={{ fontSize: 20 }}>{title}</h2>
        <p style={{ color: 'var(--ink-dim)', fontSize: 13, marginTop: 0, marginBottom: 16 }}>
          {mode === 'signup'
            ? 'For course staff. Golfers never need an account — they join with a link.'
            : 'For course staff.'}
        </p>

        <form onSubmit={onSubmit}>
          <div className="field">
            <label htmlFor="ag-email">Email</label>
            <input
              id="ag-email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="pro@bittersweetgolf.com"
            />
          </div>

          {mode !== 'reset' && (
            <div className="field">
              <label htmlFor="ag-password">Password</label>
              <input
                id="ag-password"
                type="password"
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'signup' ? 'At least 6 characters' : ''}
              />
            </div>
          )}

          {err && (
            <p className="err" style={{ marginTop: 0 }}>
              {err}
            </p>
          )}
          {notice && (
            <p style={{ color: 'var(--ok)', fontWeight: 600, marginTop: 0 }}>{notice}</p>
          )}

          <button type="submit" disabled={busy} style={{ width: '100%', marginTop: 4 }}>
            {busy ? 'Working…' : cta}
          </button>
        </form>

        <div
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: '1px solid var(--paper-border)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 14,
            fontSize: 14,
          }}
        >
          {mode !== 'signin' && <Linkish onClick={() => switchTo('signin')}>Sign in</Linkish>}
          {mode !== 'signup' && (
            <Linkish onClick={() => switchTo('signup')}>Create an account</Linkish>
          )}
          {mode !== 'reset' && (
            <Linkish onClick={() => switchTo('reset')}>Forgot password?</Linkish>
          )}
        </div>
      </div>
    </div>
  );
}

/** A button that reads as a link — keeps the card's secondary actions from shouting. */
function Linkish({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        color: 'var(--bogey)',
        fontWeight: 600,
        textDecoration: 'underline',
      }}
    >
      {children}
    </button>
  );
}
